import { FormikErrors } from 'formik'
import { calculateProductPrice } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import { CreateProductValues } from '../../redux-toolkits/products/products.type'
import { Button } from '../button'
import { ActionChip } from '../cards/actionChip'
import { SelectProductQuantityModal } from '../modal/app_modals/selectProductQuantityModal'
import { ErrorText } from '../typography/ErrorText'
import { HeaderSubheader } from '../typography/HeaderSubheader'
import { BundleItemPins } from './bundle-item-pins'

export const ManageBundleItems = ({
  values,
  errors,
  setFieldValue,
}: {
  values: CreateProductValues
  errors?: string
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void> | Promise<FormikErrors<CreateProductValues>>
}) => {
  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  return (
    <div className="flex flex-col gap-6 pb-10 border-b border-divider-gray">
      <div className="flex flex-wrap gap-4 items-start justify-between">
        <div className="flex flex-col gap-1">
          <HeaderSubheader
            title="Bundle items"
            text={`Selects the items that will be in this bundle`}
            className="w-full md:w-fit"
          />

          {errors && <ErrorText text={`${errors}`} />}
        </div>

        <Button
          primary
          size="medium"
          label="Select items"
          onClick={() => {
            handleModalOpen('selectItemsModal')
          }}
          className="!max-w-fit ml-auto"
          disabled={false}
        />
      </div>

      <div className="flex flex-row gap-4 items-center flex-wrap">
        {values?.bundledProducts?.map((bundle, idx) => {
          const key = `subcat-${idx}`
          return (
            <ActionChip
              key={key}
              iconClassName="!size-[64px]"
              className="!bg-transparent !p-0 !gap-4"
              name={
                <p className="flex flex-col gap-1 text-sm font-normal text-sec-black">
                  <span className="text-base font-medium text-black">
                    {bundle.product.name}
                  </span>
                  <span>{calculateProductPrice(bundle.product)}</span>
                  <span>Qty: {bundle.bundleQuantity}</span>
                </p>
              }
              icon={
                bundle?.product?.images?.length > 0
                  ? bundle.product.images[0]
                  : '/assets/images/good-hand.svg'
              }
              onDismiss={() => {
                setFieldValue(
                  'bundledProducts',
                  values.bundledProducts?.filter(
                    (p) => p.product._id !== bundle.product._id,
                  ),
                )
              }}
            />
          )
        })}
      </div>

      <BundleItemPins values={values} setFieldValue={setFieldValue} />

      <SelectProductQuantityModal
        open={modalOpen && modal === 'selectItemsModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={(products) => {
          // Items that stay in the bundle keep their pin on the bundle image.
          const pins = new Map(
            (values.bundledProducts ?? []).map((i) => [i.product._id, i.pin]),
          )
          setFieldValue(
            'bundledProducts',
            products.map((i) => ({
              product: i.product,
              bundleQuantity: i.quantity,
              pin: pins.get(i.product._id) ?? null,
            })),
          )
          handleModalClose()
        }}
        selectedProducts={(values.bundledProducts ?? []).map((i) => ({
          product: i.product,
          quantity: i.bundleQuantity,
        }))}
        query={{
          isBundleProduct: false,
        }}
      />
    </div>
  )
}
